import { useLazyQuery } from '@apollo/client';
import { showToast } from 'components/common/CustomToast';
import { READ_CHARGING_RECEIPT_PDF } from 'graphql/queries/readChargingReceiptPdf';
import { READ_PARKING_RECEIPT_PDF } from 'graphql/queries/readParkingReceiptPdf';
import {
  Query,
  QueryReadChargingSessionPdfArgs,
  QueryReadParkingReceiptPdfArgs,
} from 'types/generatedSchemaTypes';
import {
  convertBase64DataToPDF as convertBase64DataToPDFUrl,
  openPDF,
} from 'utils/commonUtils';
import Logger from 'utils/Logger';

const useDownloadSessionPdf = () => {
  const [readParkingReceiptPdf, { loading: loadingPdf }] = useLazyQuery<
    Query,
    QueryReadParkingReceiptPdfArgs
  >(READ_PARKING_RECEIPT_PDF, {
    fetchPolicy: 'cache-and-network',
    onCompleted: async (data) => {
      const base64PdfFile = data.readParkingReceiptPdf?.base64PdfFile;
      const parkingId = data.readParkingReceiptPdf?.sessionId ?? '';
      const type = 'application/pdf';
      if (!base64PdfFile) {
        return;
      }
      const pdfUrl = convertBase64DataToPDFUrl(base64PdfFile, type, parkingId);
      openPDF(pdfUrl, base64PdfFile, type, (error) => {
        showToast('error.downloadPdfFailed', 'error');
        Logger.error(`Saving pdf for session ${parkingId} failed | ${error}`);
      });
    },
    onError: () => showToast('error.downloadPdfFailed', 'error'),
  });

  const [readChargingSessionPdf, { loading: loadingChargingPdf }] =
    useLazyQuery<Query, QueryReadChargingSessionPdfArgs>(
      READ_CHARGING_RECEIPT_PDF,
      {
        fetchPolicy: 'cache-and-network',
        onCompleted: async (data) => {
          const base64PdfFile = data.readChargingSessionPdf?.base64PdfFile;
          const chargingId =
            data.readChargingSessionPdf?.chargingSessionId ?? '';
          const type = 'application/pdf';
          if (!base64PdfFile) {
            return;
          }
          const pdfUrl = convertBase64DataToPDFUrl(
            base64PdfFile,
            type,
            chargingId
          );
          openPDF(pdfUrl, base64PdfFile, type, (error) => {
            showToast('error.downloadPdfFailed', 'error');
            Logger.error(
              `Saving pdf for session ${chargingId} failed | ${error}`
            );
          });
        },
        onError: () => showToast('error.downloadPdfFailed', 'error'),
      }
    );

  return {
    readChargingSessionPdf,
    readParkingReceiptPdf,
    loading: loadingPdf || loadingChargingPdf,
  };
};

export default useDownloadSessionPdf;
